import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import CustomForm, { ICustomFormProps, IFormContentProp } from '../../../globalUtils/CustomForm/CustomForm';
import { cloneDeep } from 'lodash';
import { CheckBox, CustomBreadCrumbs, CustomButtonGroup, CustomDialog, DashboardHeader, DashboardLoader, useAppDispatch, useAppSelector, useEffectOnce } from '../../../globalUtils/globalExports';
import { useLocation, useNavigate } from 'react-router-dom';
import './addSoftwareVersion.css'
import CustomUpload from '../../../globalUtils/CustomUploadModified/CustomUpload';
import axios, { AxiosResponse } from 'axios'
import { Id, toast } from 'react-toastify'
import { fetchPreSignedURLForUpload } from '../../../rmsReduxStore/chargersRedux/chargerCreators';
import { createSoftwareVersion, fetchVersionClassificationReferenceData, fetchVersionProductSubTypeReferenceData, fetchVersionProductTypeReferenceData, fetchVersionSeverityReferenceData, fetchVersionSwTypeReferenceData, getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import { checkValidAlphaNumericAndDot, checkValidForFTPString, createBreadCrumbProps, keyPressValidationForFTPString } from '../../../globalUtils/globalHooks';
import { Icon, IconWithTooltip } from '../../../globalUtils/globalIcons';
import CustomChartPopup from '../dashboard/CustomChartPopup';
import access from '../../../auth/service/AccessControl';

interface AddSoftwareVersionForm {
  id: string | null;
  softwareVersion: string;
  url: string;
  description: string;
  classification: IGeneralReferenceData | null;
  severity: IGeneralReferenceData | null;
  releaseDate: string;
  productType: IGeneralReferenceData | null;
  productSubType: IGeneralReferenceData | null;
  swType: IGeneralReferenceData | null;
  releaseDocument: string;
  customerRelease: string;

}

interface IPreSignedUrlInfo {
  blobName: string;
  message: string;
  url: string;
}


const AddSoftwareVersion: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()

  const initialFormData: AddSoftwareVersionForm = {
    id: null,
    softwareVersion: '',
    url: '',
    description: '',
    classification: null,
    severity: null,
    productType: null,
    productSubType:null,
    swType: null,
    releaseDate: new Date().toISOString().split('T')[0],
    releaseDocument: '',
    customerRelease: 'Disabled'
  }

  const [mode,setMode] = useState(location?.state?.mode ? location?.state?.mode : 'add');
  const [formData, setFormData] = useState<AddSoftwareVersionForm>(initialFormData)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preSignedUrl, setPreSignedUrl] = useState<IPreSignedUrlInfo | null>(null);
  const [showLoader, setShowLoader] = useState(false)
  const { versionProductTypeReferenceData, versionSeverityReferenceData, versionClassificationReferenceData,versionReleasesProductSubTypeReferenceData,versionReleasesSwTypeReferenceData } = useAppSelector(getReferenceDataReducer)
  const versionData = location?.state?.versionData;

  if(!access?.softwareVersionReleases?.addSoftwareVersion?.add() && !versionData){
      if(access?.newDashboard?.view() && !access?.isSuperAdmin()) navigate('/newDashboard');
      else navigate('/dashboard');
  }

  const [showDialog, setShowDialog] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [showUploadFile, setShowUploadeFile] = useState(mode==='add');
  const [isSoftwareVersionFieldDisabled,setIsSoftwareVersionFieldDisabled] = useState(mode==='edit' || mode === 'view');

  useEffectOnce(() => {
    dispatch(fetchVersionProductTypeReferenceData())
    dispatch(fetchVersionClassificationReferenceData())
    dispatch(fetchVersionSeverityReferenceData())
    dispatch(fetchVersionProductSubTypeReferenceData())
    dispatch(fetchVersionSwTypeReferenceData())
  })

  const formSuccessToast = (message): Id => toast.success(message)
  const formErrorToast = (message,id): Id => toast.error(message,{toastId: `add-software-version-error-toast-${id}`});


  const handleFormDataChange = useCallback((value, formKey) => {
    const localFormData = cloneDeep(formData);
    if(formKey==='productType'){
      if(value!=='Harmony Gen1.0')localFormData['productSubType'] = null;
      if(value!=='Harmony Gen2.0')localFormData['swType'] = null;
    }
    localFormData[formKey] = value;
    setFormData(localFormData);
  }, [formData])

  // Handle Form Submit
  const handleSubmit = useCallback(async () => {
    if (Object.keys(formData).some(key => !formData[key] && key != 'description' && key != 'releaseDocument' && key != 'id' && ((formData?.productType?.name ==='Harmony Gen2.0' && key==='swType') || (formData?.productType?.name ==='Harmony Gen1.0' && key==='productSubType')))) {
      formErrorToast('All fields are required.','submit-error')
      return;
    }
    setShowLoader(true)
    const addSoftwareVersionRequest = {
      id: formData.id,
      softwareVersion: formData.softwareVersion,
      ftpUrl: formData.url,
      description: formData.description,
      classification: formData.classification?.value,
      severity: formData.severity?.value,
      releaseDate: formData.releaseDate,
      productType: formData.productType?.name,
      productSubType: formData?.productSubType?.value ?? '',
      swType: formData?.swType?.value ?? '',
      releaseDocument: formData.releaseDocument,
      customerRelease: formData.customerRelease
    }
    if (preSignedUrl && selectedFile) {
      handleFileUpload(preSignedUrl?.url, selectedFile)
    }
    const response: AxiosResponse = await dispatch(createSoftwareVersion(addSoftwareVersionRequest))
    if (response?.status === 200 || response?.status === 202) {
      setShowLoader(false)
      if(mode==='edit')formSuccessToast('Software version release is updated.')
      else formSuccessToast('Software version release is created.')
      setTimeout(() => {
        navigate('/manage-software-version-releases')
      }, 2000)
    } else {
      setShowLoader(false)
      formErrorToast(response?.data?.message,'response-error')
      if(response?.status === 400  && response?.data?.message?.includes('Software Version must start with "V"')){
        setIsSoftwareVersionFieldDisabled(false);
      }
    }
  }, [formData])


  const handleFileUpload = async (signedURL: string, file: File): Promise<void> => {
    try {
      await axios.put(signedURL, file, {
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'BlockBlob'
        }
      })
    } catch (error) {
      setSelectedFile(null)
      console.log('Error uploading file:', error)
    }
  }


  const handleFileChange = async (file): Promise<void> => {
    if (file) {
      setSelectedFile(file)
      const response = await dispatch(fetchPreSignedURLForUpload({ fileName: file?.name }))
      setPreSignedUrl(response?.data)
      handleFormDataChange(response?.data?.blobName, 'releaseDocument')
    } else setSelectedFile(null);
  }


  // Version Release Form Fields
  const versionInfoFormArray: IFormContentProp[] = useMemo(() => [
    {
      rowCountClassName: 'grid-2',
      formObject: [
        {
          formLabel: 'Software Version',
          isRequired: true,
          objectType: 'input',
          selectDropdownProps: null,
          inputProps: {
            inputPlaceHolder: 'Enter Software Version',
            handleInputChange: (event): void => {
              if (checkValidAlphaNumericAndDot(event?.target?.value)) handleFormDataChange(event?.target?.value, 'softwareVersion');
              else formErrorToast('Invalid software version.','version-error')
            },
            inputValue: formData?.softwareVersion,
            inputType: 'string',
            inputFieldId: 'add_software_version',
            isRequired: true,
            autoFocus: true,
            isDisabled: isSoftwareVersionFieldDisabled
          },
          textAreaProps: null,
        },
        {
          formLabel: 'FTP Software Upgrade Value (URL)',
          isRequired: true,
          objectType: 'input',
          selectDropdownProps: null,
          inputProps: {
            inputPlaceHolder: 'Enter FTP URL',
            handleInputChange: (event): void => {
              handleFormDataChange(checkValidForFTPString(event?.target?.value), 'url')
            },
            handleKeyPress: (event) => { keyPressValidationForFTPString(event) },
            inputValue: formData?.url,
            inputType: 'string',
            inputFieldId: 'add_ftp_url',
            isRequired: true,
            autoFocus: true,
            isDisabled: mode==='view',
          },
          textAreaProps: null
        }
      ]
    },
    {
      rowCountClassName: 'grid',
      formObject: [
        {
          formLabel: 'Description',
          isRequired: false,
          objectType: 'text-area',
          selectDropdownProps: null,
          inputProps: null,
          textAreaProps: {
            inputPlaceHolder: 'Enter Description',
            handleInputChange: (event): void => {
              if (event?.target?.value.length <= 1000) {
                handleFormDataChange(event?.target?.value, 'description')
              }
              else formErrorToast('Description can be up to 1000 characters only.','description-error')
            },
            inputValue: formData?.description,
            inputFieldId: 'add_version_description',
            isRequired: true,
            isDisabled: mode==='view',
          },

        }
      ]
    },
    {
      rowCountClassName: formData?.productType?.name==='Harmony Gen1.0' || formData?.productType?.name==='Harmony Gen2.0' ? 'grid-5':'grid-4',
      formObject: [
        {
          formLabel: 'Classification',
          isRequired: true,
          objectType: 'dropdown',
          selectDropdownProps: {
            selectValue: formData?.classification,
            selectOptions: versionClassificationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFormDataChange(val, 'classification')
            },
            selectDropDownId: 'classification-dropdown',
            selectDropDownName: 'classification-dropdown',
            isDisabled: mode === 'view',
            renderToBody: true
          },
          inputProps: null,
          textAreaProps: null
        },
        {
          formLabel: 'Severity',
          isRequired: true,
          objectType: 'dropdown',
          selectDropdownProps: {
            selectValue: formData?.severity,
            selectOptions: versionSeverityReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFormDataChange(val, 'severity')
            },
            selectDropDownId: 'severity-dropdown',
            selectDropDownName: 'severity-dropdown',
            isDisabled: mode === 'view',
            renderToBody: true,
          },
          inputProps: null,
          textAreaProps: null
        },
        {
          formLabel: 'Product Type',
          isRequired: true,
          objectType: 'dropdown',
          selectDropdownProps: {
            selectValue: formData?.productType,
            selectOptions: versionProductTypeReferenceData || [],
            handleSelectOptionChange: (val): void => {
              handleFormDataChange(val, 'productType')
            },
            selectDropDownId: 'productType-dropdown',
            selectDropDownName: 'productType-dropdown',
            isDisabled: mode === 'view' || mode === 'edit',
            renderToBody: true
          },
          inputProps: null,
          textAreaProps: null

        },
        {
          formLabel: 'Product Sub Type',
          isRequired: true,
          objectType: 'dropdown',
          isAccessible: formData?.productType?.name === 'Harmony Gen1.0',
          selectDropdownProps: {
            selectValue: formData?.productSubType,
            selectOptions: versionReleasesProductSubTypeReferenceData?.filter(item=>item.name!=='All') || [],
            handleSelectOptionChange: (val): void => {
              handleFormDataChange(val, 'productSubType')
            },
            selectDropDownId: 'productSubType-dropdown',
            selectDropDownName: 'productSubType-dropdown',
            isDisabled: mode === 'view',
            renderToBody: true
          },
          inputProps: null,
          textAreaProps: null
        },
        {
          formLabel: 'SW Type',
          isRequired: true,
          objectType: 'dropdown',
          isAccessible: formData?.productType?.name === 'Harmony Gen2.0',
          selectDropdownProps: {
            selectValue: formData?.swType,
            selectOptions: versionReleasesSwTypeReferenceData?.filter(item=>item.name!=='All') || [],
            handleSelectOptionChange: (val): void => {
              handleFormDataChange(val, 'swType')
            },
            selectDropDownId: 'swType-dropdown',
            selectDropDownName: 'swType-dropdown',
            isDisabled: mode === 'view',
            renderToBody: true
          },
          inputProps: null,
          textAreaProps: null
        },
        {
          formLabel: 'Release Date',
          isRequired: true,
          objectType: 'input',
          selectDropdownProps: null,
          inputProps: {
            inputType: 'date',
            inputValue: formData?.releaseDate
              ? new Date(formData.releaseDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0],
            inputFieldId: 'release-date',
            handleInputChange: (event): void => {
              handleFormDataChange(event.target.value, 'releaseDate')
            },
            isRequired: true,
            isDisabled: mode === 'view' || (mode === 'edit' && versionData?.url),
            restrictFutureDate:true
          },
          textAreaProps: null

        },
      ]
    }
  ], [versionReleasesProductSubTypeReferenceData,versionProductTypeReferenceData, versionSeverityReferenceData, versionClassificationReferenceData, formData,isSoftwareVersionFieldDisabled,versionReleasesSwTypeReferenceData])

  const versionInfoFormProps: ICustomFormProps = useMemo(() => {
    return {
      formContainerClassName: 'software__version__release__form',
      formArray: versionInfoFormArray,
    }
  }, [formData, versionInfoFormArray])



  const disableButton = useCallback(() => {
    //Disable the add button if any field is empty
    if (mode === 'view') return true;
    const { softwareVersion, url, severity, productType, classification, releaseDate,productSubType,swType } = formData;
    if (!softwareVersion || !url || !severity?.id || !productType?.id || !classification?.id || !releaseDate) return true;
    if(productType?.name==='Harmony Gen1.0' && !productSubType?.id)return true;
    if(productType?.name=== 'Harmony Gen2.0' && !swType?.id)return true;
    return false;
  }, [formData, selectedFile])

  const btnsList = useMemo(() => {
    return [
      {
        buttonText: mode==='edit' ? 'Update' : 'Add',
        buttonId: 'add',
        btnClassName: disableButton() ? 'primary__btn disabled' : 'primary__btn',
        handleClick: (): void => {
          handleSubmit();
        },
        isDisabled: disableButton(),
        buttonVariant: 'filled'
      },
      {
        buttonText: 'Cancel',
        buttonId: 'cancel',
        btnClassName: 'secondary__btn',
        handleClick: (): void => {
          navigate('/manage-software-version-releases')
        },
        isDisabled: false,
        buttonVariant: 'outlined'
      }
    ]
  }, [formData, selectedFile])


  useEffect(() => {
    if (mode === 'view' || mode === 'edit') {
      setFormData(prev => ({
        ...prev,
        id: versionData.id,
        softwareVersion: versionData.softwareVersion,
        url: versionData.url,
        description: versionData.description,
        classification: versionClassificationReferenceData?.filter(item => item.name === versionData.classification)[0] as IGeneralReferenceData,
        severity: versionSeverityReferenceData?.filter(item => item.name === versionData.severity)[0] as IGeneralReferenceData,
        releaseDate: versionData.releaseDate,
        productType: versionProductTypeReferenceData?.filter(item => item.name === versionData.productType)[0] as IGeneralReferenceData,
        productSubType: versionData.productType!=='Harmony Gen1.0'? null : versionReleasesProductSubTypeReferenceData?.filter(item=>item.name === versionData.productSubType)[0] as IGeneralReferenceData,
        swType:versionData.productType!=='Harmony Gen2.0' ? null : versionReleasesSwTypeReferenceData?.filter(item=>item.name === versionData?.swType)?.[0] as IGeneralReferenceData,
        releaseDocument: versionData.releaseDocument,
        customerRelease: versionData.customerRelease
      }))
      setPreSignedUrl(versionData.preSignedUrl)
    }
  }, [mode, versionData, versionClassificationReferenceData, versionSeverityReferenceData, versionProductTypeReferenceData,versionReleasesProductSubTypeReferenceData,versionReleasesSwTypeReferenceData])

  const breadCrumbs = createBreadCrumbProps({
    breadCrumbProps:
      [
        {
          objectType: 'link',
          id: 'softwareVersionRelease',
        },
        {
          objectType: 'text',
          id: 'text',
          text: mode==='view' || mode === 'edit' ? versionData?.softwareVersion : 'Add Software Version'
        }

      ]
  })


  const handleCustomerReleaseCheckboxClicked = () => {
    if (mode === 'view' || (mode === 'edit' && formData?.customerRelease === 'Enabled')) return;
    if (formData.customerRelease === 'Enabled') {
      handleFormDataChange('Disabled', 'customerRelease');
    } else setShowDialog(true);
  }

  const handleDialogClose = () => {
    setShowDialog(false);
  }

  const handleDialogSubmit = () => {
    handleFormDataChange('Enabled', 'customerRelease');
    setShowDialog(false);
  }

  return (
    <>
      <DashboardLoader showLoader={showLoader} />
      <div className='add__software__version__form__wrap'>
        <CustomBreadCrumbs breadCrumbs={breadCrumbs} />
        <div className='add__software__version__form__header'>
          <DashboardHeader header={mode==='view' ? 'View Software Version' : mode === 'edit'? 'Edit Software Version' :'Add Software Version'}
          headerClassName='add__software__version__form__wrap-header'
        />
        {mode !== 'Add' && <Icon iconClassName={mode === 'view' && access?.softwareVersionReleases?.addSoftwareVersion?.edit()? 'rms__pencil__icon' : 'rms__view__icon'} containerClassName='org__form_header-edit' 
        onIconClick={() => {
              setShowUploadeFile(false);
              if(mode==='view' && access?.softwareVersionReleases?.addSoftwareVersion?.edit() )setMode('edit')
              else setMode('view')
        }} />
        }
        </div>
        <div className='add__software__version__form__wrap-content'>
          <CustomForm {...versionInfoFormProps} />
          <div className='add__software__version__form__wrap-content-upload'>
            {showUploadFile ? <CustomUpload
              accept='.doc,
              .docx,
              application/msword,
              application/vnd.openxmlformats-officedocument.wordprocessingml.document,
              .pdf,
              application/pdf'
              className='add_version_release-document'
              onFileSelect={(file) => {
                handleFileChange(file)
              }}
              placeholder='Upload Software Version Document'
              isClosable={mode === 'edit'}
              handlCloseBtnClick={()=>{setShowUploadeFile(false)}}
            />
              :
              <div className='add__software__version__form__wrap-content-upload-preview'>
                <div className='add__software__version__form__wrap-content-upload-preview-left'>
                  <span className='add__software__version__uploaded-file-label'>Release Document: </span>
                  <span className={preSignedUrl?.blobName ? 'add__software__version__uploaded-file-name' : 'add__software__version__uploaded-file-name-disabled'}>{preSignedUrl?.blobName ? preSignedUrl?.blobName : 'No file found'}</span>
                </div>
                <div className='add__software__version__upload-file-action-btn'>
                  <div className='add__software__version__upload-file-action-view-btn' onClick={() => {
                    if (!preSignedUrl?.url) return;
                    setShowFilePreview(true)
                  }}
                  >
                    <Icon iconClassName='rms__view__icon' />
                  </div>
                  {
                    mode === 'edit' && access?.softwareVersionReleases?.addSoftwareVersion?.edit() && <div className='add__software__version__upload-file-action-view-btn'
                      onClick={() => {
                        if (mode === 'view') return;
                        setShowUploadeFile(true)
                      }
                      }
                    >
                      <Icon iconClassName='rms__pencil__icon' />
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
        <div className='add__software__version__form__wrap-customer-release'>
          <div className='add__software__version__form__wrap-customer-release-top'>
            <span className='add__software__version__form__wrap-customer-release-label'>Release Type:</span>
            <span className='add__software__version__form__wrap-customer-release-value'>{formData?.customerRelease === 'Disabled' ? 'Internal testing release' : 'Customer live release'}</span>
          </div>
          <div className='add__software__version__form__wrap-customer-release-bottom'>
            <div className='add__software__version__form__wrap-customer-release-bottom-checkbox' onClick={handleCustomerReleaseCheckboxClicked}>
              <CheckBox checked={formData?.customerRelease === 'Enabled'} isDisabled={mode === 'view' || (mode === 'edit' && versionData?.customerRelease === 'Enabled')} />
              Mark the release type as <strong>Customer live release</strong>
            </div>
            <IconWithTooltip iconClassName='rms_info_icon-black' toolTipText={'By default, the release type is set as \'Internal testing release\' until it is marked as \'Customer live release\'.'} containerClassName='add__software__version__form__wrap-customer-release-bottom-tooltip' />
          </div>
        </div>
        <div className='add__software__version__form__wrap-footer'>
          <CustomButtonGroup
            buttonsList={btnsList}
            buttonGroupClassName='button__group__footer' />
        </div>
      </div>
      <CustomDialog
        dialogConfig={{
          dialogDescription: 'Once the release type is marked as \'Customer Live release\', it can\'t be undone!',
          dialogCancelTitle: 'Cancel',
          dialogOkTitle: 'Change',
        }}
        show={showDialog}
        handleClose={handleDialogClose}
        handleSubmit={handleDialogSubmit}
      />
      <CustomChartPopup
        show={showFilePreview}
        handleClose={() => { setShowFilePreview(false) }}
      >
        <iframe src={preSignedUrl?.url} className='add__software__version__uploaded__file__preview' />
      </CustomChartPopup>
    </>
  )

}

export default AddSoftwareVersion